import { useMemo } from 'react';
import { Coffee, Sparkles, AlertTriangle, Info, Database } from 'lucide-react';
import {
  Button,
  Card,
  CardBody,
  EmptyState,
  Input,
  PageHeader,
  Select,
  Spinner,
  cn,
  type SelectOption,
} from '../../ui';
import { MarkdownView } from '../Knowledge/MarkdownView';
import { EntityRefLinks } from '../common/EntityRefLinks';
import { useCatchMeUp } from '../../features/intelligence/useCatchMeUp';
import { presetLabel, formatWhen } from '../../features/intelligence/intelligenceModel';
import type { CatchMeUpPreset } from '../../features/intelligence/types';

const PRESETS: CatchMeUpPreset[] = [
  'yesterday',
  'lastWeek',
  'lastSprint',
  'myLeave',
  'custom',
];

/**
 * Catch Me Up — get back up to speed after time away. Pick a "since" point and
 * generate a briefing (goals changed, workstream progress, stories completed,
 * changes, docs, decisions, blockers introduced/resolved, forecast changes)
 * from the entities created or updated since then, via REAL Bedrock.
 */
export function CatchMeUpContent() {
  const {
    preset,
    setPreset,
    customDate,
    setCustomDate,
    needsDate,
    sinceIso,
    sinceCount,
    result,
    loading,
    generating,
    error,
    useKb,
    setUseKb,
    generate,
  } = useCatchMeUp();

  const presetOptions = useMemo<SelectOption[]>(
    () => PRESETS.map((p) => ({ value: p, label: presetLabel(p) })),
    [],
  );

  return (
    <div>
      <Card className="mb-5">
        <CardBody className="flex flex-wrap items-end gap-3">
          <div className="w-52">
            <Select
              label="Catch me up…"
              value={preset}
              options={presetOptions}
              onChange={(e) => setPreset(e.target.value as CatchMeUpPreset)}
              disabled={generating}
            />
          </div>
          {needsDate && (
            <div className="w-44">
              <Input
                type="date"
                label={preset === 'myLeave' ? 'My last working day' : 'Since date'}
                value={customDate}
                onChange={(e) => setCustomDate(e.target.value)}
                disabled={generating}
              />
            </div>
          )}
          <button
            type="button"
            onClick={() => setUseKb(!useKb)}
            disabled={generating}
            title="Augment the briefing with knowledge-base passages"
            className={cn(
              'inline-flex items-center gap-1.5 self-end rounded-lg border px-3 py-2 text-xs font-medium transition-colors disabled:opacity-50',
              useKb
                ? 'border-accent/40 bg-accent-soft/60 text-zinc-100'
                : 'border-surface-border bg-surface-1 text-zinc-400 hover:text-zinc-200',
            )}
          >
            <Database className="h-3.5 w-3.5" /> KB context
          </button>
          <Button
            onClick={() => void generate()}
            loading={generating}
            disabled={loading}
            leftIcon={!generating ? <Sparkles className="h-4 w-4" /> : undefined}
          >
            Catch me up
          </Button>
          <span className="ml-auto self-center text-xs text-zinc-500">
            Since {formatWhen(sinceIso)} · {sinceCount}{' '}
            {sinceCount === 1 ? 'entity' : 'entities'}
          </span>
        </CardBody>
      </Card>

      <div className="mb-5 flex items-start gap-2 rounded-xl border border-surface-border bg-surface-1/60 px-4 py-3 text-sm text-zinc-400">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-zinc-500" aria-hidden="true" />
        <p>
          The briefing summarizes only what changed since your chosen date, from
          the team's stored entities. It never invents activity and produces no
          productivity or ranking metrics.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <Spinner label="Loading team context" />
        </div>
      ) : error ? (
        <EmptyState
          icon={<AlertTriangle className="h-6 w-6" />}
          title="Couldn't load team context"
          description={error}
        />
      ) : !result ? (
        <EmptyState
          icon={<Coffee className="h-6 w-6" />}
          title="Ready when you are"
          description="Choose how far back to look and generate your catch-up briefing."
        />
      ) : (
        <div className="space-y-5">
          <Card>
            <CardBody>
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2 border-b border-surface-border pb-3">
                <h2 className="text-sm font-semibold text-zinc-100">
                  {presetLabel(result.preset)}
                </h2>
                <span className="text-xs text-zinc-500">
                  since {formatWhen(result.sinceIso)}
                </span>
              </div>
              <MarkdownView markdown={result.markdown} />
            </CardBody>
          </Card>

          <Card>
            <CardBody>
              <EntityRefLinks
                refs={result.usedRefs}
                heading={`What changed (${result.usedRefs.length})`}
                emptyLabel="Nothing changed in this window."
                grouped
              />
            </CardBody>
          </Card>
        </div>
      )}
    </div>
  );
}

/**
 * Catch Me Up — legacy standalone route. The unified Recap page now hosts this
 * as its "Catch Me Up" tab; this wrapper renders the page title +
 * {@link CatchMeUpContent}.
 */
export function CatchMeUpPage() {
  return (
    <div>
      <PageHeader
        icon={<Coffee className="h-5 w-5" />}
        title="Catch Me Up"
        description="Been away? Get a fast, grounded briefing of everything that changed since a date you choose."
      />
      <CatchMeUpContent />
    </div>
  );
}

export default CatchMeUpPage;
